import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BarChartIcon, CreditCardIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon, MegaphoneIcon, HomeIcon, BedDoubleIcon, ClipboardListIcon, DollarSignIcon, BookOpenIcon, TruckIcon, FileIcon, AlertTriangleIcon, RefreshCwIcon, ShieldIcon } from 'lucide-react';
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout
  } = useAuth();
  const isActive = (path: string) => {
    if (path === '/overview') {
      return location.pathname === '/overview' || location.pathname === '/';
    }
    return location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return location.pathname === '/payment-requests';
  };
  // Check if any transactions path is active
  const isTransactionsActive = () => {
    return ['/sales', '/settlements', '/disputes', '/refunds'].some(path => location.pathname.startsWith(path));
  };
  // Get the current page title based on the URL
  const getPageTitle = () => {
    if (location.pathname === '/overview' || location.pathname === '/') return 'Overview';
    if (location.pathname === '/payment-requests') return 'Payment Requests';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/orders') return 'Orders';
    if (location.pathname === '/tutorials') return 'Tutorials';
    if (location.pathname === '/campaigns') return 'Campaigns';
    if (location.pathname === '/campaign-details') return 'Campaign Details';
    if (location.pathname === '/campaign-form') return location.search.includes('id=') ? 'Update Campaign' : 'Create Campaign';
    if (location.pathname === '/invoices') return 'Invoices';
    if (location.pathname.startsWith('/invoice-detail/')) return 'Invoice Details';
    if (location.pathname === '/invoice-form') return location.search.includes('id=') ? 'Edit Invoice' : 'Create Invoice';
    if (location.pathname === '/customers') return 'Customers';
    if (location.pathname.startsWith('/customer-detail/')) return 'Customer Details';
    if (location.pathname === '/customer-form') return location.search.includes('id=') ? 'Edit Customer' : 'Create Customer';
    if (location.pathname === '/sales') return 'Sales';
    if (location.pathname.startsWith('/sale-detail/')) return 'Sale Details';
    if (location.pathname === '/settlements') return 'Settlements';
    if (location.pathname.startsWith('/settlement-detail/')) return 'Settlement Details';
    if (location.pathname === '/disputes') return 'Disputes';
    if (location.pathname.startsWith('/dispute-detail/')) return 'Dispute Details';
    if (location.pathname === '/refunds') return 'Refunds';
    if (location.pathname.startsWith('/refund-detail/')) return 'Refund Details';
    if (location.pathname === '/suppliers') return 'Suppliers';
    if (location.pathname.startsWith('/supplier-detail/')) return 'Supplier Details';
    if (location.pathname === '/supplier-form') return location.search.includes('id=') ? 'Edit Supplier' : 'Create Supplier';
    if (location.pathname === '/bills') return 'Bills';
    if (location.pathname.startsWith('/bill-detail/')) return 'Bill Details';
    if (location.pathname === '/bill-form') return location.search.includes('id=') ? 'Edit Bill' : 'Create Bill';
    if (location.pathname.startsWith('/bill-approval/')) return 'Bill Approval';
    if (location.pathname.startsWith('/bill-financing/')) return 'Request Bill Financing';
    if (location.pathname.startsWith('/tutorial/')) return 'Tutorial';
    return 'Dashboard';
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-246">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 right-0 p-4 z-20" data-id="element-247">
        <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-md bg-white shadow-sm border border-gray-200" data-id="element-248">
          {menuOpen ? <XIcon size={20} data-id="element-249" /> : <MenuIcon size={20} data-id="element-250" />}
        </button>
      </div>
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 w-64 bg-gray-100 border-r border-gray-300 z-10 transform transition-transform duration-200 ease-in-out
        ${menuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-id="element-251">
        <div className="h-full flex flex-col" data-id="element-252">
          {/* Logo */}
          <div className="p-4 border-b border-gray-300 flex items-center justify-center flex-shrink-0" data-id="element-253">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/c3ZBwzojuSYrA5qxKsGMb1/logo_geniebusiness.svg" alt="Genie Business" className="h-12" data-id="element-254" />
          </div>
          {/* Navigation */}
          <nav className="flex-1 py-4 space-y-1 text-gray-700 overflow-y-auto" data-id="element-255">
            <button onClick={() => navigate('/overview')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/overview') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-256">
              <BarChartIcon size={18} className="mr-3" data-id="element-257" />
              Overview
            </button>
            <button onClick={() => navigate('/payment-requests')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/payment-requests') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-258">
              <LayoutGridIcon size={18} className="mr-3" data-id="element-259" />
              Payment requests
            </button>
            {/* Terminals Collapsible Menu */}
            <div data-id="element-260">
              <button onClick={() => setTerminalsExpanded(!terminalsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-261">
                <div className="flex items-center" data-id="element-262">
                  <TerminalIcon size={18} className="mr-3" data-id="element-263" />
                  Terminals
                </div>
                <ChevronDownIcon size={16} className={`transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} data-id="element-264" />
              </button>
              {/* Terminal submenu items */}
              {terminalsExpanded && <div className="ml-7 pl-4 border-l border-gray-300" data-id="element-265">
                  <button onClick={() => navigate('/virtual-terminal')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/virtual-terminal') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-266">
                    <BanknoteIcon size={16} className="mr-3" data-id="element-267" />
                    Virtual
                  </button>
                  <button onClick={() => navigate('/physical-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/physical-terminals') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-268">
                    <CreditCardIcon size={16} className="mr-3" data-id="element-269" />
                    Physical
                  </button>
                  <button onClick={() => navigate('/softpos-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/softpos-terminals') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-270">
                    <SmartphoneIcon size={16} className="mr-3" data-id="element-271" />
                    SoftPos
                  </button>
                  <button onClick={() => navigate('/connect')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/connect') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-272">
                    <GlobeIcon size={16} className="mr-3" data-id="element-273" />
                    Connect
                  </button>
                </div>}
            </div>
            <button onClick={() => navigate('/invoices')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/invoices') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-274">
              <FileTextIcon size={18} className="mr-3" data-id="element-275" />
              Invoices
            </button>
            <button onClick={() => navigate('/bills')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-276">
              <FileIcon size={18} className="mr-3" data-id="element-277" />
              Bills
            </button>
            <div className="border-t border-gray-300" data-id="element-278"></div>
            <button onClick={() => navigate('/customers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/customers') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-279">
              <UsersIcon size={18} className="mr-3" data-id="element-280" />
              Customers
            </button>
            <button onClick={() => navigate('/suppliers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-281">
              <TruckIcon size={18} className="mr-3" data-id="element-282" />
              Suppliers
            </button>
            <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isTransactionsActive() ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-283">
              <div className="flex items-center" data-id="element-284">
                <ArrowLeftRightIcon size={18} className="mr-3" data-id="element-285" />
                Transactions
              </div>
              <ChevronDownIcon size={16} className={`transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} data-id="element-286" />
            </button>
            {/* Transactions submenu items */}
            {transactionsExpanded && <div className="ml-7 pl-4 border-l border-gray-300" data-id="element-287">
                <button onClick={() => navigate('/sales')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/sales') || location.pathname.startsWith('/sale-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-288">
                  <CreditCardIcon size={16} className="mr-3" data-id="element-289" />
                  Sales
                </button>
                <button onClick={() => navigate('/settlements')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/settlements') || location.pathname.startsWith('/settlement-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-290">
                  <BanknoteIcon size={16} className="mr-3" data-id="element-291" />
                  Settlements
                </button>
                <button onClick={() => navigate('/disputes')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/disputes') || location.pathname.startsWith('/dispute-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-292">
                  <AlertTriangleIcon size={16} className="mr-3" data-id="element-293" />
                  Disputes
                </button>
                <button onClick={() => navigate('/refunds')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/refunds') || location.pathname.startsWith('/refund-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-294">
                  <RefreshCwIcon size={16} className="mr-3" data-id="element-295" />
                  Refunds
                </button>
              </div>}
            <button onClick={() => navigate('/shops')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/shops') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-296">
              <ShoppingBagIcon size={18} className="mr-3" data-id="element-297" />
              Shops
            </button>
            <button onClick={() => navigate('/orders')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/orders') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-298">
              <ShoppingCartIcon size={18} className="mr-3" data-id="element-299" />
              Orders
            </button>
            <button onClick={() => navigate('/campaigns')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-300">
              <MegaphoneIcon size={18} className="mr-3" data-id="element-301" />
              Campaigns
            </button>
            <div className="border-t border-gray-300" data-id="element-302"></div>
            <button onClick={() => navigate('/settings')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/settings') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-303">
              <SettingsIcon size={18} className="mr-3" data-id="element-304" />
              Settings
            </button>
            <button onClick={() => navigate('/tutorials')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/tutorials') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-305">
              <GraduationCapIcon size={18} className="mr-3" data-id="element-306" />
              Tutorials
            </button>
          </nav>
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 lg:pl-64" data-id="element-307">
        <div className="bg-white border-b border-gray-200" data-id="element-308">
          <div className="flex justify-between items-center px-6 py-4" data-id="element-309">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-310">
              {getPageTitle()}
            </h1>
            <div className="relative" data-id="element-311">
              <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-312">
                <div className="w-10 h-10 bg-[#f8d7d7] rounded-full flex items-center justify-center text-[#e54c3c] font-medium" data-id="element-313">
                  SV
                </div>
                <div className="ml-3 hidden sm:block text-left" data-id="element-314">
                  <p className="text-sm font-medium text-gray-800" data-id="element-315">
                    Simon Verraest
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-316">CEYLON TEA CO.</p>
                </div>
                <ChevronDownIcon size={16} className={`ml-2 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} data-id="element-317" />
              </button>
              {/* User Dropdown Menu */}
              {userMenuOpen && <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} data-id="element-318" />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-319">
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings?tab=company');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-320">
                      <UsersIcon size={16} className="mr-3 text-gray-500" data-id="element-321" />
                      Account
                    </button>
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings?tab=company');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-322">
                      <BarChartIcon size={16} className="mr-3 text-gray-500" data-id="element-323" />
                      Report
                    </button>
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-324">
                      <SettingsIcon size={16} className="mr-3 text-gray-500" data-id="element-325" />
                      Settings
                    </button>
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings?tab=security');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-326">
                      <ShieldIcon size={16} className="mr-3 text-gray-500" data-id="element-327" />
                      Security
                    </button>
                    <div className="border-t border-gray-200 my-1" data-id="element-328" />
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                  navigate('/');
                }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-329">
                      <LogOutIcon size={16} className="mr-3" data-id="element-330" />
                      Logout
                    </button>
                  </div>
                </>}
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-331">
          {children}
        </div>
      </div>
      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-0 lg:hidden" onClick={() => setMenuOpen(false)} data-id="element-332"></div>}
    </div>;
}